import { Capacitor } from '@capacitor/core';

import { getAdminTabToken } from '@/lib/admin-tab-token';
import { getAuthToken } from '@/lib/auth-token';
import {
  consumeExpiredImpersonation,
  endExpiredImpersonation,
  getImpersonationToken,
} from '@/lib/impersonation-token';

/**
 * Bearer-token auth is for NATIVE and LOCAL DEV only — never production web,
 * which authenticates with the same-site HttpOnly session cookie (ENG-577 H4).
 * See `api-client.ts` for the full reasoning.
 */
export const usesBearerAuth = (): boolean =>
  Capacitor.isNativePlatform() || import.meta.env.DEV;

/**
 * The ONE answer to "who is this tab?", for every request the app makes —
 * `apiClient`'s auth provider and every hand-rolled `fetch` / chat transport.
 *
 *   1. The tab's impersonation token (`impersonation-token.ts`) — this tab
 *      became a customer.
 *   2. The admin's own tab token (`admin-tab-token.ts`) — this tab is in the
 *      admin terminal.
 *   3. The native/dev bearer (`auth-token.ts`) — never on production web.
 *
 * `undefined` means "send no Authorization header; the cookie decides".
 *
 * WHY ONE FUNCTION (ENG-1529). Hand-rolled transports used to send
 * `getAuthToken()` directly, which skips 1 and 2. On production web that is
 * undefined, so those requests rode the shared session cookie — and in an
 * impersonating tab, Claire chat, lead export and the campaign composer ran as
 * whichever customer ANOTHER tab impersonated last. The architecture test in
 * `request-auth-token.test.ts` keeps raw `getAuthToken()` out of request code.
 *
 * Also the choke point for an impersonation that ran out: rather than letting
 * the tab fall through to 2/3/the cookie — a different identity than the one
 * on screen — it is sent back to the admin panel.
 */
export function getRequestAuthToken(): string | undefined {
  const impersonationToken = getImpersonationToken();
  if (impersonationToken) return impersonationToken;
  if (consumeExpiredImpersonation()) endExpiredImpersonation();
  return getAdminTabToken() ?? (usesBearerAuth() ? getAuthToken() : undefined);
}

/** `{ Authorization }` for a hand-rolled request, or `{}`. */
export function requestAuthHeaders(): Record<string, string> {
  const token = getRequestAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}
